import { useState, useEffect, useCallback, useRef } from "react";
import { API_URL } from "../../lib/constants";
import { getValidAccessToken } from "../../lib/api/Auth";
import { isSuperAdmin } from "@common/domain/auth";

const BASE_LAST_SEEN_ERROR_KEY = "last_seen_system_error_timestamp";

export function getStorageKey(userId?: string): string {
  return userId ? `${BASE_LAST_SEEN_ERROR_KEY}_${userId}` : BASE_LAST_SEEN_ERROR_KEY;
}

export function getStoredLastSeenTimestamp(userId?: string): string | null {
  try {
    const key = getStorageKey(userId);
    const value = localStorage.getItem(key);
    if (value) return value;
    // Fallback to base un-scoped key if user-scoped key is not yet set
    return userId ? localStorage.getItem(BASE_LAST_SEEN_ERROR_KEY) : null;
  } catch {
    return null;
  }
}

export function setStoredLastSeenTimestamp(timestamp: string, userId?: string): void {
  try {
    const newTime = new Date(timestamp).getTime();
    if (isNaN(newTime)) return; // Reject invalid dates to avoid corrupting storage

    const key = getStorageKey(userId);
    const current = getStoredLastSeenTimestamp(userId);
    if (current) {
      const currentTime = new Date(current).getTime();
      if (!isNaN(currentTime) && currentTime >= newTime) {
        return; // Strictly monotonic: do not regress to an older timestamp
      }
    }
    localStorage.setItem(key, timestamp);
    window.dispatchEvent(new CustomEvent("system_errors_seen", { detail: { timestamp, userId } }));
  } catch {
    // Ignore localStorage errors (e.g., in incognito or restricted environments)
  }
}

export interface UseSystemErrorAlertResult {
  hasNewErrors: boolean;
  latestErrorTimestamp: string | null;
  markErrorsAsSeen: (latestTimestamp?: string) => void;
  checkNewErrors: () => Promise<void>;
}

export function useSystemErrorAlert(userInfo: any, pollIntervalMs = 60000): UseSystemErrorAlertResult {
  const [hasNewErrors, setHasNewErrors] = useState(false);
  const [latestErrorTimestamp, setLatestErrorTimestamp] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const isAdmin = Boolean(userInfo && isSuperAdmin(userInfo.groups));
  const userId = userInfo?.userId || userInfo?.user_id || userInfo?.sub || userInfo?.email;

  const checkNewErrors = useCallback(async () => {
    if (!isAdmin) {
      if (isMountedRef.current) setHasNewErrors(false);
      return;
    }

    try {
      const token = await getValidAccessToken();
      if (!token || !isMountedRef.current) return;

      // Bound startDate to past 7 days (or since lastSeen) to avoid 90-day DynamoDB partition scans
      const lastSeen = getStoredLastSeenTimestamp(userId);
      const defaultStartDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const lastSeenMs = lastSeen ? new Date(lastSeen).getTime() : NaN;
      const startDate = !isNaN(lastSeenMs)
        ? new Date(Math.min(lastSeenMs, Date.now())).toISOString()
        : defaultStartDate;

      const params = new URLSearchParams({
        resolved: "false",
        limit: "1",
        startDate,
      });

      const response = await fetch(`${API_URL}/admin/errors?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok || !isMountedRef.current) return;

      const data = await response.json();
      if (!isMountedRef.current) return;

      const entries = data.entries || [];
      if (entries.length > 0) {
        const newestUnresolved = entries[0];
        const errorTimestamp =
          newestUnresolved.timestamp || newestUnresolved.createdAt || newestUnresolved.metadata?.timestamp;
        if (errorTimestamp) {
          setLatestErrorTimestamp(errorTimestamp);
        }

        const freshLastSeen = getStoredLastSeenTimestamp(userId);
        const lastSeenTime = freshLastSeen ? new Date(freshLastSeen).getTime() : NaN;
        const errorTime = errorTimestamp ? new Date(errorTimestamp).getTime() : NaN;

        if (!isNaN(errorTime)) {
          if (isNaN(lastSeenTime) || errorTime > lastSeenTime) {
            setHasNewErrors(true);
          } else {
            setHasNewErrors(false);
          }
        } else if (!freshLastSeen) {
          setHasNewErrors(true);
        } else {
          setHasNewErrors(false);
        }
      } else {
        setHasNewErrors(false);
      }
    } catch {
      // Ignore network errors during background check
    }
  }, [isAdmin, userId]);

  const markErrorsAsSeen = useCallback((latestTimestamp?: string) => {
    const timestampToStore = latestTimestamp || latestErrorTimestamp || new Date().toISOString();
    setStoredLastSeenTimestamp(timestampToStore, userId);
    setHasNewErrors(false);
  }, [latestErrorTimestamp, userId]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!isAdmin) {
      setHasNewErrors(false);
      return () => {
        isMountedRef.current = false;
      };
    }

    checkNewErrors();

    const interval = setInterval(() => {
      checkNewErrors();
    }, pollIntervalMs);

    const handleFocus = () => {
      checkNewErrors();
    };
    const handleSeenEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      const eventUserId = customEvent.detail?.userId;
      if (!eventUserId || !userId || eventUserId === userId) {
        if (isMountedRef.current) setHasNewErrors(false);
      }
    };
    const handleStorageEvent = (e: StorageEvent) => {
      const expectedKey = getStorageKey(userId);
      if (e.key === expectedKey || e.key === BASE_LAST_SEEN_ERROR_KEY) {
        checkNewErrors();
      }
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("system_errors_seen", handleSeenEvent);
    window.addEventListener("storage", handleStorageEvent);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("system_errors_seen", handleSeenEvent);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [isAdmin, userId, checkNewErrors, pollIntervalMs]);

  return {
    hasNewErrors,
    latestErrorTimestamp,
    markErrorsAsSeen,
    checkNewErrors,
  };
}
